import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import { getChatCompletion } from '../../../services/aiIntegrations/chatCompletion';

const AddVendorSearchModal = ({ isOpen, onClose, project, onAddVendor }) => {
  const [isFinding, setIsFinding] = useState(false);
  const [aiSuggestions, setAiSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [manualForm, setManualForm] = useState({ name: '', website: '', price: '', notes: '' });
  const [manualError, setManualError] = useState('');

  if (!isOpen) return null;

  const buildNeedsSummary = () => {
    const specs = project?.specifications?.specs || {};
    const purchaseType = project?.purchaseType || project?.specifications?.purchaseType || project?.category || '';
    const budget = project?.budget ? `$${project?.budget?.toLocaleString()}` : (project?.specifications?.budgetRange || '');
    const zipCode = project?.zipCode || '';
    const notes = project?.notes || project?.specifications?.notes || '';
    const timeline = project?.timeline || project?.specifications?.timeline || '';

    const specLines = Object.entries(specs)
      ?.filter(([key, val]) => !['weights', 'weightings', 'quoteSettings']?.includes(key) && val != null && String(val)?.trim() !== '')
      ?.map(([key, val]) => `- ${key}: ${val}`);

    let summary = `Purchase Type: ${purchaseType}\n`;
    if (budget) summary += `Budget: ${budget}\n`;
    if (zipCode) summary += `Location/Zip: ${zipCode}\n`;
    if (timeline) summary += `Timeline: ${timeline}\n`;
    if (notes) summary += `Additional Notes: ${notes}\n`;
    if (specLines?.length > 0) {
      summary += `\nProduct Requirements:\n${specLines?.join('\n')}`;
    }
    return summary;
  };

  const handleFindOnline = async () => {
    setIsFinding(true);
    setShowSuggestions(false);
    setAiSuggestions([]);

    try {
      const needsSummary = buildNeedsSummary();
      const purchaseType = project?.purchaseType || project?.category || 'product';

      const systemPrompt = `You are a shopping research assistant. The user is looking to purchase a ${purchaseType}.
Search the web for real, currently available products or services that match their needs.
Return ONLY a valid JSON object with no markdown, no explanation, no code fences, no reasoning text.
The JSON must have this exact structure:
{
  "options": [
    {
      "name": "Product/Service Name",
      "vendor": "Retailer or Seller Name",
      "price": "299.99",
      "productUrl": "https://exact-product-page-url.com",
      "description": "Brief description of why this matches the user's needs",
      "keySpecs": "Key specifications relevant to the user's requirements",
      "reviewScore": "4.5",
      "reviewCount": "1200"
    }
  ]
}
Provide 3-5 real options. Use exact product page URLs (not homepage URLs). Price should be a number string without currency symbols.
For hotels, resorts, vacation packages, and travel services: ALWAYS include the Google review rating (e.g. "4.3") in reviewScore and the number of Google reviews (e.g. "2847") in reviewCount. These are critical fields — do not omit them.
For all other products/services: include reviewScore and reviewCount from Google, Yelp, Trustpilot, or any authoritative review source if available.`;

      const userMessage = `Find me the best online options for the following purchase:\n\n${needsSummary}\n\nReturn only the JSON object with real product URLs.`;

      const response = await getChatCompletion(
        'PERPLEXITY',
        'perplexity/sonar-pro',
        [
          { role: 'system', content: systemPrompt },
          { role: 'user', content: userMessage }
        ],
        { max_tokens: 2000, temperature: 0.3 }
      );

      const content = response?.choices?.[0]?.message?.content || response;
      let parsed;
      if (typeof content === 'string') {
        let cleaned = content?.replace(/<think>[\s\S]*?<\/think>/gi, '')?.trim();
        cleaned = cleaned?.replace(/```json\s*/gi, '')?.replace(/```\s*/g, '')?.trim();
        const jsonMatch = cleaned?.match(/\{[\s\S]*\}/);
        if (!jsonMatch) throw new Error('No JSON object found in response');
        parsed = JSON.parse(jsonMatch?.[0]);
      } else {
        parsed = content;
      }

      const options = parsed?.options || [];
      if (options?.length === 0) {
        alert('No options found. Try refining your project needs.');
        return;
      }

      setAiSuggestions(options);
      setShowSuggestions(true);
    } catch (err) {
      console.error('Find Vendors error:', err);
      alert('Failed to find vendors. Please try again.');
    } finally {
      setIsFinding(false);
    }
  };

  const handleAddSuggested = (opt) => {
    onAddVendor({
      store: opt?.vendor || opt?.name,
      name: opt?.name,
      price: opt?.price || '',
      url: opt?.productUrl || '',
      description: opt?.description || '',
      keySpecs: opt?.keySpecs || '',
      condition: null,
      deal: [opt?.description, opt?.keySpecs]?.filter(Boolean)?.join(' | '),
      reviewScore: opt?.reviewScore ? parseFloat(opt?.reviewScore) : null,
      reviewCount: opt?.reviewCount ? parseInt(String(opt?.reviewCount)?.replace(/[^0-9]/g, ''), 10) : null
    });
  };

  const validatePrice = (price) => /^\d+(\.\d{1,2})?$/?.test(price) || price === '';

  const handleManualAdd = () => {
    if (!manualForm?.name?.trim()) {
      setManualError('Vendor / option name is required.');
      return;
    }
    if (manualForm?.price && !validatePrice(manualForm?.price)) {
      setManualError('Please enter a valid price (e.g. 299.99).');
      return;
    }
    setManualError('');
    onAddVendor({
      store: manualForm?.name?.trim(),
      name: manualForm?.name?.trim(),
      price: manualForm?.price || '',
      url: manualForm?.website || '',
      description: manualForm?.notes || '',
      keySpecs: '',
      condition: null,
      deal: manualForm?.notes || ''
    });
    setManualForm({ name: '', website: '', price: '', notes: '' });
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ background: 'rgba(0,0,0,0.45)' }}>
      <div
        className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90vh] flex flex-col overflow-hidden"
        style={{ border: '1.5px solid #fde8d8' }}
      >
        {/* Header */}
        <div
          className="px-6 py-5 flex items-center justify-between flex-shrink-0"
          style={{ borderBottom: '1px solid #fde8d8' }}
        >
          <div className="flex items-center gap-3">
            <div
              className="w-10 h-10 rounded-xl flex items-center justify-center"
              style={{ background: 'linear-gradient(135deg, #F58A54, #F06F6B)' }}
            >
              <Icon name="UserPlus" size={20} color="white" />
            </div>
            <div>
              <h2 className="text-lg font-bold text-gray-900">Add Vendor</h2>
              <p className="text-xs text-gray-500">Search online or add manually</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="w-8 h-8 rounded-full flex items-center justify-center text-gray-400 hover:bg-gray-100 transition-colors"
          >
            <Icon name="X" size={18} />
          </button>
        </div>

        {/* Scrollable body */}
        <div className="flex-1 overflow-y-auto p-6 space-y-6">

          {/* Search Online Section */}
          <div>
            <div className="flex items-center justify-between mb-3">
              <div>
                <h3 className="text-sm font-semibold text-gray-800">Search Online</h3>
                <p className="text-xs text-gray-500 mt-0.5">AI-powered search based on your project needs</p>
              </div>
              <button
                onClick={handleFindOnline}
                disabled={isFinding}
                className="flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-bold text-white transition-all hover:opacity-90 disabled:opacity-60"
                style={{ background: 'linear-gradient(135deg, #F58A54, #F06F6B)' }}
              >
                {isFinding ? (
                  <>
                    <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24" fill="none">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z" />
                    </svg>
                    Searching...
                  </>
                ) : (
                  <>
                    <Icon name="Search" size={14} color="white" />
                    Find Options
                  </>
                )}
              </button>
            </div>

            {/* AI Suggestions Panel */}
            {showSuggestions && aiSuggestions?.length > 0 && (
              <div className="rounded-2xl overflow-hidden shadow-lg border border-orange-100">
                {/* Panel Header */}
                <div
                  className="px-5 py-4 flex items-center justify-between"
                  style={{ background: 'linear-gradient(135deg, #F06F6B 0%, #F07A5E 50%, #F58A54 100%)' }}
                >
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 rounded-xl bg-white/20 backdrop-blur-sm flex items-center justify-center">
                      <Icon name="Sparkles" size={16} color="white" />
                    </div>
                    <div>
                      <h4 className="font-bold text-white text-sm tracking-wide">AI-Suggested Options</h4>
                      <p className="text-white/75 text-xs">{aiSuggestions?.length} matches found based on your needs</p>
                    </div>
                  </div>
                  <button
                    onClick={() => { setShowSuggestions(false); setAiSuggestions([]); }}
                    className="w-7 h-7 rounded-full bg-white/20 hover:bg-white/30 flex items-center justify-center transition-colors"
                  >
                    <Icon name="X" size={14} color="white" />
                  </button>
                </div>

                {/* Cards */}
                <div className="bg-gray-50 p-4 space-y-3">
                  {aiSuggestions?.map((opt, idx) => (
                    <div key={idx} className="bg-white rounded-xl border border-gray-100 shadow-sm hover:shadow-md transition-shadow overflow-hidden">
                      <div className="h-0.5 w-full" style={{ background: 'linear-gradient(to right, #F06F6B, #F58A54)' }} />
                      <div className="p-4 flex items-start gap-3">
                        <div
                          className="flex-shrink-0 w-7 h-7 rounded-lg flex items-center justify-center text-xs font-bold text-white"
                          style={{ background: 'linear-gradient(135deg, #F06F6B, #F58A54)' }}
                        >
                          {idx + 1}
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-start justify-between gap-2">
                            <div className="min-w-0">
                              <p className="font-semibold text-gray-900 text-sm leading-tight">{opt?.name}</p>
                              {opt?.vendor && (
                                <p className="text-xs text-gray-400 mt-0.5 font-medium uppercase tracking-wide">{opt?.vendor}</p>
                              )}
                            </div>
                            {opt?.price && (
                              <span
                                className="flex-shrink-0 text-xs font-bold px-2.5 py-1 rounded-full"
                                style={{ background: 'linear-gradient(135deg, #FFF0EE, #FFF5F0)', color: '#F06F6B', border: '1px solid #FECDC8' }}
                              >
                                ${opt?.price}
                              </span>
                            )}
                          </div>
                          {opt?.description && (
                            <p className="text-xs text-gray-500 mt-2 leading-relaxed line-clamp-2">{opt?.description}</p>
                          )}
                          {opt?.keySpecs && (
                            <div className="mt-2 flex items-start gap-1">
                              <Icon name="CheckCircle" size={11} className="flex-shrink-0 mt-0.5" style={{ color: '#F58A54' }} />
                              <p className="text-xs text-gray-400 italic line-clamp-1">{opt?.keySpecs}</p>
                            </div>
                          )}
                          <div className="flex items-center justify-between mt-3">
                            {opt?.productUrl ? (
                              <a
                                href={opt?.productUrl}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="inline-flex items-center gap-1 text-xs font-medium hover:underline transition-colors"
                                style={{ color: '#F06F6B' }}
                              >
                                <Icon name="ExternalLink" size={11} />
                                View Product
                              </a>
                            ) : <span />}
                            <button
                              onClick={() => handleAddSuggested(opt)}
                              className="inline-flex items-center gap-1.5 text-xs font-semibold text-white px-3 py-1.5 rounded-lg transition-opacity hover:opacity-90 active:scale-95"
                              style={{ background: 'linear-gradient(135deg, #F06F6B, #F58A54)' }}
                            >
                              <Icon name="Plus" size={12} color="white" />
                              Add to Comparison
                            </button>
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>

                {/* Footer */}
                <div className="bg-gray-50 px-5 py-3 border-t border-gray-100 flex items-center gap-2">
                  <Icon name="Info" size={12} style={{ color: '#F58A54' }} />
                  <p className="text-xs text-gray-400">Results are added as online vendors to your comparison table.</p>
                </div>
              </div>
            )}
          </div>

          {/* Divider */}
          <div className="flex items-center gap-3">
            <div className="flex-1 h-px bg-gray-200" />
            <span className="text-xs text-gray-400 font-medium">or add manually</span>
            <div className="flex-1 h-px bg-gray-200" />
          </div>

          {/* Manual Add Section */}
          <div className="space-y-4">
            <h3 className="text-sm font-semibold text-gray-800">Add Manually</h3>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="flex flex-col gap-1">
                <label className="text-xs font-medium text-gray-600">Vendor / Option Name <span className="text-red-400">*</span></label>
                <input
                  type="text"
                  placeholder="e.g., Amazon, Local Store"
                  value={manualForm?.name}
                  onChange={(e) => setManualForm({ ...manualForm, name: e?.target?.value })}
                  className="px-3 py-2 rounded-xl border text-sm focus:outline-none focus:ring-2 transition-all"
                  style={{ borderColor: '#fde8d8', focusRingColor: '#F58A54' }}
                />
              </div>

              <div className="flex flex-col gap-1">
                <label className="text-xs font-medium text-gray-600">Website / Product URL</label>
                <input
                  type="url"
                  placeholder="https://example.com/product"
                  value={manualForm?.website}
                  onChange={(e) => setManualForm({ ...manualForm, website: e?.target?.value })}
                  className="px-3 py-2 rounded-xl border text-sm focus:outline-none focus:ring-2 transition-all"
                  style={{ borderColor: '#fde8d8' }}
                />
              </div>

              <div className="flex flex-col gap-1">
                <label className="text-xs font-medium text-gray-600">Price (Optional)</label>
                <input
                  type="text"
                  placeholder="299.99"
                  value={manualForm?.price}
                  onChange={(e) => setManualForm({ ...manualForm, price: e?.target?.value })}
                  className="px-3 py-2 rounded-xl border text-sm focus:outline-none focus:ring-2 transition-all"
                  style={{ borderColor: '#fde8d8' }}
                />
              </div>

              <div className="flex flex-col gap-1">
                <label className="text-xs font-medium text-gray-600">Notes</label>
                <input
                  type="text"
                  placeholder="Any notes about this vendor..."
                  value={manualForm?.notes}
                  onChange={(e) => setManualForm({ ...manualForm, notes: e?.target?.value })}
                  className="px-3 py-2 rounded-xl border text-sm focus:outline-none focus:ring-2 transition-all"
                  style={{ borderColor: '#fde8d8' }}
                />
              </div>
            </div>

            {manualError && (
              <p className="text-xs text-red-500">{manualError}</p>
            )}

            <div className="flex justify-end">
              <button
                onClick={handleManualAdd}
                className="flex items-center gap-2 px-5 py-2 rounded-xl text-sm font-bold text-white transition-all hover:opacity-90"
                style={{ background: 'linear-gradient(135deg, #F58A54, #F06F6B)' }}
              >
                <Icon name="Plus" size={14} color="white" />
                Add to Comparison
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AddVendorSearchModal;
